import {useSelector} from 'react-redux'
import {useNightlyForAllRooms} from 'components/Accommodation/hooks/useNightlyForAllRooms'
import {getPriceValue} from 'modules/accommodation/utils'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getShowTotalPrices,
  getSplitBookingDetails,
  getSplitBookingPriceSummary
} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {getAllOffersWithSplitBookings} from 'modules/sapiSearch/utils'

import {TOP_OFFERS_COUNT} from './TopOffers/Desktop/TopOffersDesktopLayout'
import {useAccommodationTopOffers} from './useAccommodationTopOffers'

/**
 * Finds the next cheapest offer position by comparing the total prices of the offers
 * There's an exception for SB - offers has only first SB offer on the list
 * so we need its index to be passed separately to know which one is it when doing comparison
 *
 * Don't export, USE internally only
 * @param offers - offers with split booking offer[0] injected
 * @param totalSbPrice - total price of the split booking offer
 * @param sbOfferIndex - index of the split booking offer
 * @returns index of the next cheapest offer
 */
const getNonTopCheapestOfferPosition = (
  offers: SearchOffer[],
  totalSbPrice: number,
  sbOfferIndex: number
) => {
  if (offers.length === 0) return 0

  const flatTotalPrices = offers.map((offer, index) =>
    index !== sbOfferIndex ? offer.totalRate : totalSbPrice
  )

  const nonTopOffersFlatPrices = flatTotalPrices.slice(TOP_OFFERS_COUNT)

  if (nonTopOffersFlatPrices.length === 0) return 0

  return (
    nonTopOffersFlatPrices.indexOf(Math.min(...nonTopOffersFlatPrices)) +
    TOP_OFFERS_COUNT
  )
}

export const useGetNonTopCheapestOfferPrice = ({
  hotelId
}: {
  hotelId: string
}) => {
  const showTotalPrices = useSelector(getShowTotalPrices)
  const {offerEntity, hasAnyOffers} = useAccommodationTopOffers({
    hotelId
  })
  const currency = useSelector(getCurrencyCode)
  const nightlyForAllRooms = useNightlyForAllRooms(hotelId)
  const splitBookingDetails = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const isLoading = !hasAnyOffers

  const allOffersWithSplitBookings = getAllOffersWithSplitBookings(
    offerEntity,
    splitBookingDetails
  )

  const {hasSplitBookingOffer, splitBookingPosition} = splitBookingDetails
  const sbPriceDetails = useSelector(state =>
    getSplitBookingPriceSummary(state, hotelId)
  )

  const nextCheapestOfferPosition = getNonTopCheapestOfferPosition(
    allOffersWithSplitBookings,
    sbPriceDetails.sum,
    splitBookingPosition
  )

  const isSplitBookingCheapest =
    hasSplitBookingOffer && splitBookingPosition === nextCheapestOfferPosition

  const offerValue = getPriceValue(
    allOffersWithSplitBookings[nextCheapestOfferPosition],
    showTotalPrices,
    sbPriceDetails,
    isSplitBookingCheapest
  )

  const value =
    showTotalPrices || isSplitBookingCheapest
      ? offerValue
      : nightlyForAllRooms(offerValue)

  return {value, currency, isLoading}
}
